import React, { useState, useEffect, useRef, useCallback } from "react";
import produce from "immer";
import {
  List,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  AutoSizer,
} from "react-virtualized";
import { ResponseRow } from "components/response";
import { NotFound, Loading } from "components/Utils";
import styles from "./Resultset.module.css";

const pageSize = 20;

const Resultset = ({
  className,
  detailId,
  setDetail,
  status,
  result,
  execute,
  RowComponent = ResponseRow,
}) => {
  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 100,
    })
  );

  const [pages, setPages] = useState([]);

  const list = useRef(null);

  // Update virtualized list on resize
  useEffect(() => {
    const forceUpdate = () => {
      cache.current.clearAll();
      list.current && list.current.forceUpdateGrid();
    };
    window.addEventListener("resize", forceUpdate);
    return () => window.removeEventListener("resize", forceUpdate);
  }, []);

  // Update virtualized list on result changes
  useEffect(() => {
    cache.current.clearAll();
    list.current && list.current.forceUpdateGrid();
  }, [result]);

  const loadMoreRows = useCallback(
    ({ startIndex, stopIndex }) => {
      if (
        Object.prototype.hasOwnProperty.call(result.resultset, startIndex) &&
        Object.prototype.hasOwnProperty.call(result.resultset, stopIndex) &&
        (startIndex < 0 || stopIndex < pageSize)
      ) {
        return;
      }
      let page = Math.floor(startIndex / pageSize) + 1;
      if (page * pageSize < stopIndex) {
        ++page;
      }
      !pages.includes(page) &&
        execute(page, true).then(
          (r) => r && setPages(pages.filter((p) => p !== page))
        );
      setPages([...pages, page]);
    },
    [execute, result.resultset, pages]
  );

  const isRowLoaded = useCallback(({ index }) => !!result.resultset[index], [
    result.resultset,
  ]);

  /**
  const rowRerender = useCallback((index) => {
    cache.current.clear(index, 0);
    list.current && list.current.recomputeRowHeights(index);
  }, []);
 */
  const getResponse = useCallback(
    (index) =>
      result.resultset[index] &&
      produce(result.resultset[index], (response) => {
        response.persons = response.owners || response.authors || null;
      }),
    [result.resultset]
  );

  const rowRenderer = useCallback(
    ({ index, parent, key, style }) => {
      const response = getResponse(index);
      const toggleDetail = () =>
        setDetail(response && detailId !== response.id ? response : null);

      return (
        <CellMeasurer
          key={key}
          cache={cache.current}
          parent={parent}
          columnIndex={0}
          rowIndex={index}
        >
          <RowComponent
            className={
              response && detailId === response.id ? "active" : "default"
            }
            title={`${(
              index + 1
            ).toLocaleString()}/${result.docsCount.toLocaleString()}`}
            response={response}
            toggleDetail={toggleDetail}
            style={style}
          />
        </CellMeasurer>
      );
    },
    [detailId, getResponse, setDetail, result.docsCount]
  );

  const infiniteLoaderChildren = useCallback(
    ({ onRowsRendered, registerChild }) =>
      result &&
      result.resultset && (
        <AutoSizer>
          {({ width, height }) => (
            <List
              rowCount={result.docsCount}
              width={width}
              height={height}
              rowHeight={cache.current.rowHeight}
              rowRenderer={rowRenderer}
              deferredMeasurementCache={cache.current}
              overscanRowCount={10}
              onRowsRendered={onRowsRendered}
              ref={(ref) => {
                list.current = ref;
                registerChild(ref);
              }}
            />
          )}
        </AutoSizer>
      ),
    [result, rowRenderer]
  );

  if (!result.resultset) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  if (result.resultset.length < 1) {
    return (
      <div className={styles.notFound}>
        <NotFound items="Documents" />
      </div>
    );
  }

  return (
    <div className={className}>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        minimumBatchSize={pageSize}
        threshold={pageSize}
        loadMoreRows={loadMoreRows}
        rowCount={result.docsCount}
        children={infiniteLoaderChildren}
      />
    </div>
  );
};

export { Resultset };
